import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export type GalleryShot = {
  src: string;
  alt: string;
  caption?: string;
};

const AUTO_MS = 4500;

export function ProductGallery({
  screenshots,
  productName,
  showZoom = true,
}: {
  screenshots: GalleryShot[];
  productName: string;
  showZoom?: boolean;
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);
  const total = screenshots.length;

  const goTo = useCallback(
    (next: number) => {
      if (total === 0) return;
      setIndex(((next % total) + total) % total);
    },
    [total],
  );

  const prev = useCallback(() => goTo(index - 1), [goTo, index]);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);

  useEffect(() => {
    setIndex(0);
  }, [screenshots]);

  useEffect(() => {
    if (paused || total <= 1) return;
    const id = window.setInterval(() => {
      setIndex((current) => (current + 1) % total);
    }, AUTO_MS);
    return () => window.clearInterval(id);
  }, [paused, total, screenshots]);

  if (total === 0) return null;

  const current = screenshots[index];

  return (
    <>
      <section
        className="product-gallery"
        aria-roledescription="carousel"
        aria-label={`${productName} screenshots`}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setPaused(false);
          }
        }}
      >
        <div className="product-gallery-header">
          <h3>Screenshots</h3>
          <span className="muted product-gallery-count">
            {index + 1} / {total}
            {current.caption ? ` · ${current.caption}` : ''}
          </span>
        </div>

        <div className="product-gallery-stage">
          <button
            type="button"
            className="product-gallery-nav prev"
            onClick={prev}
            aria-label="Previous screenshot"
            disabled={total <= 1}
          >
            <span className="material-symbols-outlined">chevron_left</span>
          </button>

          <div
            className={`product-gallery-frame${showZoom ? ' is-zoomable' : ''}`}
            onClick={showZoom ? () => setZoomIndex(index) : undefined}
            role={showZoom ? 'button' : undefined}
            tabIndex={showZoom ? 0 : undefined}
            aria-label={showZoom ? 'Click to view full size' : undefined}
            onKeyDown={
              showZoom
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setZoomIndex(index);
                    }
                  }
                : undefined
            }
          >
            {screenshots.map((shot, i) => (
              <figure
                key={`${i}-${shot.src}`}
                className={`product-gallery-slide ${i === index ? 'active' : ''}`}
                aria-hidden={i !== index}
              >
                <img
                  src={shot.src}
                  alt={shot.alt}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  draggable={false}
                />
              </figure>
            ))}
            {showZoom && (
              <span className="product-gallery-zoom-hint" aria-hidden="true">
                <span className="material-symbols-outlined">zoom_in</span>
              </span>
            )}
          </div>

          <button
            type="button"
            className="product-gallery-nav next"
            onClick={next}
            aria-label="Next screenshot"
            disabled={total <= 1}
          >
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>

        <div className="product-gallery-thumbs" role="tablist" aria-label="Screenshot thumbnails">
          {screenshots.map((shot, i) => (
            <button
              key={`${i}-${shot.src}`}
              type="button"
              role="tab"
              aria-selected={i === index}
              className={`product-gallery-thumb ${i === index ? 'active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Show screenshot ${i + 1}${shot.caption ? `: ${shot.caption}` : ''}`}
            >
              <img src={shot.src} alt="" loading="lazy" draggable={false} />
            </button>
          ))}
        </div>
      </section>

      {zoomIndex !== null && (
        <FullScreenLightbox
          shots={screenshots}
          startIndex={zoomIndex}
          onClose={() => setZoomIndex(null)}
        />
      )}
    </>
  );
}

/**
 * Full-screen lightbox rendered via a portal to document.body,
 * escaping any parent stacking contexts (sticky sidebars, cards, etc).
 * Shows the image at natural dimensions with scroll only if needed.
 */
function FullScreenLightbox({
  shots,
  startIndex,
  onClose,
}: {
  shots: GalleryShot[];
  startIndex: number;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(startIndex);
  const total = shots.length;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft') {
        setIndex((c) => (c - 1 + total) % total);
      } else if (event.key === 'ArrowRight') {
        setIndex((c) => (c + 1) % total);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, total]);

  const shot = shots[index];

  return createPortal(
    <div className="fs-lightbox" role="dialog" aria-modal="true" aria-label="Full size image">
      <div className="fs-lightbox-backdrop" onClick={onClose} />
      <div className="fs-lightbox-content">
        <div className="fs-lightbox-header">
          <span className="fs-lightbox-counter">
            {index + 1} / {total}
            {shot.caption ? ` · ${shot.caption}` : ''}
          </span>
          <button
            type="button"
            className="fs-lightbox-close"
            onClick={onClose}
            aria-label="Close full size image"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {total > 1 && (
          <button
            type="button"
            className="fs-lightbox-nav prev"
            onClick={(e) => {
              e.stopPropagation();
              setIndex((c) => (c - 1 + total) % total);
            }}
            aria-label="Previous image"
          >
            <span className="material-symbols-outlined">chevron_left</span>
          </button>
        )}

        <div className="fs-lightbox-img-wrap" onClick={onClose}>
          <img
            className="fs-lightbox-img"
            src={shot.src}
            alt={shot.alt}
            draggable={false}
            onClick={(e) => e.stopPropagation()}
          />
        </div>

        {total > 1 && (
          <button
            type="button"
            className="fs-lightbox-nav next"
            onClick={(e) => {
              e.stopPropagation();
              setIndex((c) => (c + 1) % total);
            }}
            aria-label="Next image"
          >
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
